import { useState, useEffect } from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/AuthContext";
import { useGlobalLoading } from "@/context/GlobalLoadingContext";
import { useToast } from "@/components/ui/use-toast";
import { Check } from "lucide-react";
import { FiTag } from 'react-icons/fi';

const TaskLabels = ({ task, onTaskChange, onRefreshActivities, organizationSlug, newTaskAvailableLabels, newTask = false, showLabel = true }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [availableLabels, setAvailableLabels] = useState(task?.availableLabels || []);
    const { axiosInstance } = useAuth();
    const { showLoading, hideLoading } = useGlobalLoading();
    const { toast } = useToast();

    useEffect(() => {
        if (newTaskAvailableLabels) {
            setAvailableLabels(newTaskAvailableLabels);
        }
    }, [newTaskAvailableLabels]);

    const filteredLabels = availableLabels?.filter(label =>
        label.name.toLowerCase().includes(search.toLowerCase())
    ) || [];

    const handleLabelChange = async (labelToAdd) => {
        // 1. Capture old value for rollback
        const oldLabels = task.labels || [];
        
        // Check if this is a new label (no _id)
        const isNewLabel = !labelToAdd._id;

        // 2. Optimistically update UI immediately
        const optimisticTask = {
            ...task,
            labels: [...oldLabels, {
                ...labelToAdd,
                _id: isNewLabel ? `temp-${Date.now()}` : labelToAdd._id // Temporary ID for new labels
            }]
        };
        onTaskChange(optimisticTask);

        if (task.key) {
            // 3. Fire API call (don't await before UI update)
            try {
                showLoading();
                const response = await axiosInstance.post(
                    `/tasks/${organizationSlug}/${task.key}/labels`,
                    {
                        label: labelToAdd,
                        isNewLabel
                    }
                );

                // Update available labels if we created a new one
                if (isNewLabel && response.data.availableLabels) {
                    setAvailableLabels(response.data.availableLabels);
                }
                
                // Update task labels with server response (to get real IDs)
                onTaskChange({ ...task, labels: response.data.labels, availableLabels: response.data.availableLabels });

                // 4. Refresh just activities (new activity was created)
                if (onRefreshActivities) {
                    await onRefreshActivities();
                }
            } catch (error) {
                // 5. Rollback on error
                console.error('Error adding label:', error);
                onTaskChange({ ...task, labels: oldLabels });
                toast({
                    description: "Failed to add label",
                    variant: "destructive"
                });
            } finally {
                hideLoading();
            }
        }
    };

    const handleRemoveLabel = async (labelToRemove) => {
        // 1. Capture old value for rollback
        const oldLabels = task.labels || [];
        
        // 2. Optimistically update UI immediately
        const optimisticTask = {
            ...task,
            labels: oldLabels.filter(label => label._id !== labelToRemove._id)
        };
        onTaskChange(optimisticTask);

        if (task.key) {
            // 3. Fire API call (don't await before UI update)
            try {
                showLoading();
                const response = await axiosInstance.delete(
                    `/tasks/${organizationSlug}/${task.key}/labels`,
                    { data: { label: labelToRemove } }
                );

                // Update task labels with server response
                onTaskChange({ ...task, labels: response.data.labels });

                // 4. Refresh just activities (new activity was created)
                if (onRefreshActivities) {
                    await onRefreshActivities();
                }
            } catch (error) {
                // 5. Rollback on error
                console.error('Error removing label:', error);
                onTaskChange({ ...task, labels: oldLabels });
                toast({
                    description: "Failed to remove label",
                    variant: "destructive"
                });
            } finally {
                hideLoading();
            }
        }
    };

    return (
        <div className="space-y-2">
            {showLabel && <div className="text-sm font-normal text-neutral-500 dark:text-neutral-600">Labels</div>}
            <div className="flex flex-wrap gap-2">
                {task.labels && task.labels.map((label) => (
                    <Button
                        key={label._id}
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 flex items-center gap-1.5"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(true);
                        }}
                    >
                        <div className={`w-2 h-2 rounded-full ${label.color}`} />
                        {label.name}
                    </Button>
                ))}
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            size="sm"
                            className={`text-xs h-7 ${task.labels?.length ? "w-7": ""}`}
                        >
                            {task.labels?.length ? "+" : "Add label"}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-fit" align="start" sideOffset={8} side={`${newTask && "top"}`}>
                        <Command className="min-w-[100px]">
                            <CommandInput
                                placeholder="Change labels..."
                                value={search}
                                onValueChange={setSearch}
                                icon={FiTag}
                                className="w-full"
                            />
                            <CommandList>
                                {availableLabels?.length === 0 ? (
                                    <>
                                        <CommandEmpty>No labels found.</CommandEmpty>
                                        <CommandGroup>
                                            <CommandItem
                                                onSelect={() => {
                                                    handleLabelChange({ name: search, color: 'bg-gray-500' });
                                                    setSearch("");
                                                }}
                                            >
                                                Create new label: "{search}"
                                            </CommandItem>
                                        </CommandGroup>
                                    </>
                                ) : (
                                    <CommandGroup>
                                        {search &&
                                         !availableLabels.some(label =>
                                            label.name.toLowerCase() === search.toLowerCase()
                                         ) && (
                                            <CommandItem
                                                onSelect={() => {
                                                    handleLabelChange({ name: search, color: 'bg-gray-500' });
                                                    setSearch("");
                                                }}
                                                className="flex items-center gap-1.5"
                                            >
                                                <div className="w-2 h-2 rounded-full bg-gray-500" />
                                                Create new label: "{search}"
                                            </CommandItem>
                                        )}
                                        {filteredLabels.map((label) => (
                                            <CommandItem
                                                key={label._id}
                                                onSelect={() => {
                                                    if (task.labels?.some(l => l._id === label._id)) {
                                                        handleRemoveLabel(label);
                                                    } else {
                                                        handleLabelChange(label);
                                                    }
                                                }}
                                                className="flex items-center gap-1.5"
                                            >
                                                <div className={`w-2 h-2 rounded-full ${label.color}`} />
                                                {label.name}
                                                {task.labels?.some(l => l._id === label._id) && (
                                                    <Check className="ml-auto h-4 w-4" />
                                                )}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                )}
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
};

export default TaskLabels;
